import { RateOwnerType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';

import AutomaticAddOns, {
  AddOnAssignmentSchema,
  AutomaticAddOnsDTO,
  AutomaticAddOnsHandler,
} from './AutomaticAddOns';

interface AddOnFormRow {
  addOn: string;
  addOnType: string;
  rateType: string;
  costRate: string;
  invoiceRate: string;
  defaultQty: string;
}

interface AutomaticRateAddOnFormProps {
  open: boolean;
  onSave: (automaticAddOns: AutomaticAddOnsDTO) => void;
  onClose: () => void;
  rateOwnerType?: RateOwnerType;
  initialAddOns?: AddOnAssignmentSchema[];
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const AutomaticRateAddOnDialog: React.FC<AutomaticRateAddOnFormProps> = ({
  open,
  onClose,
  onSave,
  rateOwnerType,
  initialAddOns,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const automaticAddOnsRef = useRef<AutomaticAddOnsHandler>(null);

  const { handleSubmit } = useForm<AddOnFormRow>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = useCallback(async () => {
    setIsLoading(true);

    try {
      const automaticAddOns = await automaticAddOnsRef.current?.onSubmit();
      onSave(automaticAddOns as AutomaticAddOnsDTO);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <Dialog isOpen={open} onClose={onClose} maxWidth="md">
      <DialogHeader onClose={onClose}>
        {`${capitalize(rateOwnerType ?? '')} ${t('common.add_ons')}`}
      </DialogHeader>

      <DialogContent>
        <AutomaticAddOns ref={automaticAddOnsRef} initialAddOns={initialAddOns} />
      </DialogContent>

      <DialogActions>
        <SmallButton variant="outlined" color="secondary" onClick={onClose}>
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          color="primary"
          loading={isLoading}
          onClick={handleSubmit(onSubmit)}
          data-test-id="automatic-rate-add-on-dialog__save"
        >
          {t('actions.save')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AutomaticRateAddOnDialog;
